import clsx from 'clsx';
import { highlight, languages } from 'prismjs';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Editor from 'react-simple-code-editor';
import { toast } from 'react-toastify';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism.css';

import { useSourcePreview } from '@/hooks/queries/useSources';
import { ExampleTypeEnum } from '@/types/evals';

import styles from './JsonConfigForm.module.scss';
import sharedStyles from './modalSourceCreate.module.scss';
import { InlineExample, jsonConfigSchema } from './schemas';

export type JsonValidity = {
  blocked: boolean;
  message?: string;
};

type JsonConfigFieldsProps = {
  isPending?: boolean;
  isVersionMode: boolean;
  sourceId?: string;
  onValidityChange: ( validity: JsonValidity ) => void;
};

// Preview rows arrive flattened (input_msg/output_msg); convert legacy text
// rows back to the nested { type, data: [{ user, message }], meta_data } shape
// and pass already-structured rows through. Produces the shared InlineExample
// draft shape the editor and the create payload expect.
const transformPreviewToOriginal = ( item: any ): InlineExample => {
  if ( item.type === 'message' && item.data?.input_msg !== undefined ) {
    const metaData = item.data.metadata ?? item.meta_data;
    return {
      type: item.type,
      data: [
        { user: item.data.input_role || 'user', message: item.data.input_msg },
        { user: item.data.output_role || 'assistant', message: item.data.output_msg }
      ],
      ...( metaData != null ? { meta_data: metaData } : {} )
    };
  }

  return {
    type: item.type,
    data: item.data,
    ...( item.meta_data != null ? { meta_data: item.meta_data } : {} )
  };
};

const jsonPlaceholder = `{
  "examples": [
    {
      "type": "message",
      "data": [
        {
          "user": "user",
          "message": "Hello"
        },
        {
          "user": "assistant",
          "message": "Hi there!"
        }
      ]
    }
  ]
}`;

export const JsonConfigFields: React.FC<JsonConfigFieldsProps> = ( {
  isPending,
  isVersionMode,
  sourceId,
  onValidityChange
} ) => {
  const { setValue } = useFormContext();
  const [ jsonInput, setJsonInput ] = useState( '' );
  const [ parseError, setParseError ] = useState<string>();
  const [ schemaErrors, setSchemaErrors ] = useState<string[]>( [] );
  const [ exampleCount, setExampleCount ] = useState( 0 );

  // Version mode: preload the current version's examples so editing starts
  // from the existing dataset rather than a blank editor.
  const { data: preview, isLoading, error } = useSourcePreview(
    sourceId ?? '',
    'latest',
    100,
    isVersionMode && !!sourceId
  );

  const totalCount = preview?.totalCount ?? 0;
  const showWarning = isVersionMode && totalCount > 100;

  useEffect( () => {
    if ( error ) {
      toast.error( 'Failed to load source data' );
    }
  }, [ error ] );

  const applyParsed = ( value: string ) => {
    setParseError( undefined );
    setSchemaErrors( [] );

    if ( !value.trim() ) {
      setValue( 'config', { examples: [] } );
      setExampleCount( 0 );
      return;
    }

    try {
      const parsed = JSON.parse( value );
      const result = jsonConfigSchema.safeParse( parsed );

      if ( !result.success ) {
        const messages = result.error.issues.map( ( issue ) => {
          const path = issue.path.join( '.' );
          return path ? `${ path }: ${ issue.message }` : issue.message;
        } );
        setSchemaErrors( messages );
        // Still store what was parsed so the form has the latest value.
        setValue( 'config', parsed );
        setExampleCount( Array.isArray( parsed?.examples ) ? parsed.examples.length : 0 );
        return;
      }

      setValue( 'config', result.data );
      setExampleCount( result.data.examples.length );
      const detected = result.data.examples[ 0 ]?.type as ExampleTypeEnum | undefined;
      if ( detected ) setValue( 'example_type', detected );
    } catch( e ) {
      setParseError( e instanceof Error ? e.message : 'Invalid JSON format' );
    }
  };

  // Initialize editor from preloaded version data.
  useEffect( () => {
    if ( preview?.items ) {
      const examples = preview.items.map( transformPreviewToOriginal );
      const text = JSON.stringify( { examples }, null, 2 );
      setJsonInput( text );
      applyParsed( text );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ preview ] );

  const handleJsonInputChange = ( value: string ) => {
    setJsonInput( value );
    applyParsed( value );
  };

  // Report validity + alert message to the modal (which owns the footer button
  // and the bottom-left alert).
  const message = parseError ?? ( schemaErrors.length > 0 ? schemaErrors[ 0 ] : undefined );
  const blocked = !!parseError || schemaErrors.length > 0 || exampleCount === 0;
  useEffect( () => {
    onValidityChange( { blocked, message } );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ blocked, message ] );

  const hasError = !!parseError || schemaErrors.length > 0;

  return (
    <section className={ sharedStyles.config_section }>
      <h3 className={ sharedStyles.config_section_title }>Configure JSON Properties</h3>

      {showWarning && (
        <div className={ sharedStyles.warning_banner } data-testid="json-truncation-warning">
          <strong>Warning:</strong> This source has {totalCount} examples, but only the first 100 can be edited here.
          The new version will contain only the examples shown below.
        </div>
      )}

      <div className={ clsx( 'layout__col', 'pad' ) }>
        <label
          className={ clsx( 'auth_flow_label', hasError && 'required' ) }
          htmlFor="config.examples"
        >
          Examples JSON <span className={ sharedStyles.required_asterisk }>*</span>
          {isVersionMode && (
            <span className={ sharedStyles.example_count }>
              {' '}({exampleCount} example{exampleCount !== 1 ? 's' : ''})
            </span>
          )}
        </label>
        <p className={ sharedStyles.field_description }>
          Paste your examples as JSON. Format: {'{ "examples": [...] }'}
        </p>
        {isLoading ? (
          <div className={ sharedStyles.json_loading }>Loading source data...</div>
        ) : (
          <div
            className={ clsx(
              styles.json_editor_wrapper,
              hasError && sharedStyles.input_error
            ) }
          >
            <Editor
              value={ jsonInput }
              onValueChange={ handleJsonInputChange }
              highlight={ ( code ) => highlight( code, languages.json, 'json' ) }
              padding={ 10 }
              disabled={ isPending }
              placeholder={ jsonPlaceholder }
              style={ {
                fontFamily: '"Fira code", "Fira Mono", monospace',
                fontSize: 13,
                // Let the editor grow to its content; the modal body is the
                // single scroll container. A nested overflow here desyncs
                // react-simple-code-editor's absolutely-positioned textarea.
                minHeight: '400px'
              } }
              textareaId="config.examples"
              data-testid="source-config-json-examples"
            />
          </div>
        )}
      </div>
    </section>
  );
};
